import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const CHICAGO = { latitude: 41.9168256, longitude: -87.6969984 };
const ACCURACY_LIMIT = 200; // ignore fixes worse than 200m
const EMA_ALPHA = 0.3;      // smoothing: 0 = frozen, 1 = raw GPS
const STALE_MS = 10000;     // accept a less-precise fix if the best source has been silent this long

const GeolocationContext = createContext({
  position: CHICAGO,
  error: null,
  loading: true,
  source: 'loading', // 'loading' | 'ip' | 'gps' | 'fallback'
});

async function fetchIpFallback() {
  const tryFetch = async (url, extract) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    try {
      const r = await fetch(url, { signal: controller.signal });
      const d = await r.json();
      clearTimeout(timer);
      return extract(d);
    } catch {
      clearTimeout(timer);
      return null;
    }
  };

  return (
    (await tryFetch("https://ipapi.co/json/", (d) =>
      d.latitude && d.longitude
        ? { latitude: +d.latitude, longitude: +d.longitude }
        : null
    )) ||
    (await tryFetch("https://ipinfo.io/json", (d) =>
      d.loc
        ? { latitude: +d.loc.split(",")[0], longitude: +d.loc.split(",")[1] }
        : null
    )) ||
    CHICAGO
  );
}

export const GeolocationProvider = ({ children }) => {
  const [location, setLocation] = useState({
    position: CHICAGO,
    error: null,
    loading: true,
    source: 'loading',
  });
  const smoothedRef = useRef(null);
  const watchIdsRef = useRef([]);
  const bestAccuracyRef = useRef(Infinity);
  const lastAppliedAtRef = useRef(0);

  const applyUpdate = useCallback((coords) => {
    if (coords.accuracy > ACCURACY_LIMIT) return;

    const now = Date.now();
    const isMorePrecise = coords.accuracy <= bestAccuracyRef.current;
    const isStale = now - lastAppliedAtRef.current > STALE_MS;
    if (!isMorePrecise && !isStale) return; // a more/equally precise fix is still fresh — ignore this one

    bestAccuracyRef.current = coords.accuracy;
    lastAppliedAtRef.current = now;

    const raw = { latitude: coords.latitude, longitude: coords.longitude };
    smoothedRef.current = smoothedRef.current
      ? {
          latitude:
            smoothedRef.current.latitude * (1 - EMA_ALPHA) + raw.latitude * EMA_ALPHA,
          longitude:
            smoothedRef.current.longitude * (1 - EMA_ALPHA) + raw.longitude * EMA_ALPHA,
        }
      : raw;
    setLocation({ position: { ...smoothedRef.current }, error: null, loading: false, source: 'gps' });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const startWatches = () => {
      watchIdsRef.current.forEach((id) => navigator.geolocation.clearWatch(id));
      watchIdsRef.current = [];
      if (cancelled) return;
      // On GPS error, promote source to 'fallback' so consumers unblock
      const onError = () => {
        if (!cancelled) setLocation((prev) =>
          prev.source === 'gps' ? prev : { ...prev, source: 'fallback' }
        );
      };
      // Fast network-based fix — available within ~100ms
      watchIdsRef.current.push(
        navigator.geolocation.watchPosition(
          (pos) => { if (!cancelled) applyUpdate(pos.coords); },
          onError,
          { enableHighAccuracy: false, timeout: 8000, maximumAge: 5000 }
        )
      );
      // Precise GPS fix — slower but more accurate; both run in parallel
      watchIdsRef.current.push(
        navigator.geolocation.watchPosition(
          (pos) => { if (!cancelled) applyUpdate(pos.coords); },
          onError,
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
        )
      );
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) {
        // Reset smoothing — position may have drifted while tab was hidden
        smoothedRef.current = null;
        bestAccuracyRef.current = Infinity;
        lastAppliedAtRef.current = 0;
        startWatches();
      }
    };

    fetchIpFallback().then((fallback) => {
      if (cancelled) return;

      if (!navigator.geolocation) {
        setLocation({ position: fallback, error: "Geolocation not supported", loading: false, source: 'fallback' });
        return;
      }

      // Show IP-derived position immediately while GPS warms up
      setLocation((prev) =>
        prev.loading ? { position: fallback, error: null, loading: false, source: 'ip' } : prev
      );

      startWatches();
      document.addEventListener("visibilitychange", onVisibility);
    });

    return () => {
      cancelled = true;
      watchIdsRef.current.forEach((id) => navigator.geolocation.clearWatch(id));
      watchIdsRef.current = [];
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [applyUpdate]);

  return (
    <GeolocationContext.Provider value={location}>
      {children}
    </GeolocationContext.Provider>
  );
};

export const useGeolocation = () => useContext(GeolocationContext);
